import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api, { setAccessToken, setRefreshToken, clearTokens, getAccessToken } from '../lib/api';

const AuthContext = createContext(null);

const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown, false=guest, object=logged-in
  const [practice, setPractice] = useState(null);
  const [bootChecked, setBootChecked] = useState(false);
  const [expiredNotice, setExpiredNotice] = useState(false);
  const idleTimerRef = useRef(null);

  const logout = useCallback(async ({ silent = false, expired = false } = {}) => {
    try {
      await api.post('/api/auth/logout');
    } catch (_) {
      /* ignore */
    }
    clearTokens();
    setUser(false);
    setPractice(null);
    if (expired) setExpiredNotice(true);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      logout({ expired: true });
    }, IDLE_TIMEOUT_MS);
  }, [logout]);

  // Set up activity listeners only when logged in
  useEffect(() => {
    if (!user || user === false) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    const handler = () => resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  // Auth-expired event (from axios interceptor)
  useEffect(() => {
    const handler = () => logout({ expired: true });
    window.addEventListener('studioops:auth-expired', handler);
    return () => window.removeEventListener('studioops:auth-expired', handler);
  }, [logout]);

  // Boot: try /me only if we have an access_token (no cookies in this build)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!getAccessToken()) {
          if (!cancelled) setUser(false);
          return;
        }
        const r = await api.get('/api/auth/me');
        if (!cancelled) {
          setUser(r.data.user);
          setPractice(r.data.practice || null);
        }
      } catch (_) {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setBootChecked(true);
      }
    })();
    // Also mark bootChecked=true immediately when no token, to skip /me round-trip
    if (!getAccessToken()) setBootChecked(true);
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post('/api/auth/login', { email, password });
    setAccessToken(r.data.access_token);
    if (r.data.refresh_token) setRefreshToken(r.data.refresh_token);
    setUser(r.data.user);
    setPractice(r.data.practice || null);
    setExpiredNotice(false);
    return r.data;
  }, []);

  const refreshMe = useCallback(async () => {
    const r = await api.get('/api/auth/me');
    setUser(r.data.user);
    setPractice(r.data.practice || null);
  }, []);

  // Derived: the active practice id. For admin/assistant this is their own
  // practice; for superadmin it's whichever practice they've picked via the
  // LocationSwitcher (persisted in localStorage and sent as X-Practice-Id).
  const activePracticeId =
    practice?._id ||
    (user && user.role === 'superadmin' ? localStorage.getItem('studioops_active_practice') : null);

  return (
    <AuthContext.Provider
      value={{
        user,
        practice,
        setPractice,
        activePracticeId,
        bootChecked,
        expiredNotice,
        clearExpiredNotice: () => setExpiredNotice(false),
        login,
        logout,
        refreshMe,
        refresh: refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
